<template>
  <main class="main-content flex-grow-1 p-4 overflow-auto mt-6" style="background-color: #f8f9fa">
    <h2 class="text-header-page" style="color: var(--Dark-100, #022326)">
      Balance
    </h2>
    <!-- Sandbox Mode Alert -->
    <div v-if="environment === 'sandbox'" class="alert alert-warning mb-4 text-white" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      You are currently on sandbox mode, where all operations are testing
      purpose only and do not affect live data
    </div>

    <!-- Success Toast -->
    <transition name="toast">
      <div v-if="toastMessage" class="balance-toast" role="status">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" fill="#1c9762" />
          <path d="M8 12.5l2.5 2.5L16 9.5" stroke="#fff" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
        {{ toastMessage }}
      </div>
    </transition>

    <div class="balance-cards">
      <BalanceCard label="Dashboard Balance" :balance="dashboardBalance" :loading="balanceLoading"
        :needs-virtual-account="hasDashboardVa === false" :generating="generatingVa"
        @add-balance="openAddBalance('DASHBOARD')" @transfer-balance="openTransfer('dashboard')"
        @generate-va="handleGenerateVa" />
      <BalanceCard label="API Balance" :balance="apiBalance" :loading="balanceLoading"
        @add-balance="openAddBalance('API')" @transfer-balance="openTransfer('api')" />
    </div>

    <!-- Activity Table Container -->
    <div class="activity-card">
      <div class="activity-label">Activity</div>
      <BalanceActivityTable ref="balanceActivityTable" :activities="activities"
        :loading="activitiesLoading && !isExporting" :pagination-data="pagination" :initial-filters="initialFilters"
        @page-change="handlePageChange" @export-csv="handleExportCSV" />
    </div>

    <AddBalanceModal :show="showAddBalance" :app-type="addBalanceAppType" @close="showAddBalance = false" />
    <TransferBalanceModal :show="showTransferBalance" :dashboard-balance="dashboardBalance" :api-balance="apiBalance"
      :initial-source="transferSource" :submitting="transferringBalance" @close="showTransferBalance = false"
      @submit="handleTransferSubmit" />
  </main>
</template>


<script>
import BalanceCard from "./BalanceCard.vue";
import BalanceActivityTable from "./BalanceActivityTable.vue";
import AddBalanceModal from "./AddBalanceModal.vue";
import TransferBalanceModal from "./TransferBalanceModal.vue";
import { getBalance, historyBalance, getListVA, generateDashboardVA, transferBalance } from "../../api/balance";

export default {
  name: "BalanceView",
  computed: {
    environment() {
      return this.$store.getters.currentEnvironment;
    },
    isMerchantBlocked() {
      const profile = this.$store.getters["auth/userProfile"];
      return String(profile?.status || "").toLowerCase() === "blocked";
    },
  },
  components: {
    BalanceCard,
    BalanceActivityTable,
    AddBalanceModal,
    TransferBalanceModal,
  },
  data() {
    const activitiesData = [];
    return {
      dashboardBalance: null,
      apiBalance: null,
      // null while loading, then whether the merchant has a Dashboard Balance top-up VA
      hasDashboardVa: null,
      generatingVa: false,
      addBalanceAppType: "API",
      showTransferBalance: false,
      transferringBalance: false,
      transferSource: "dashboard",
      toastMessage: "",
      toastTimer: null,
      activities: activitiesData,
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1,
        next_page_url: null,
        prev_page_url: null
      },
      showAddBalance: false,
      balanceLoading: false,
      activitiesLoading: false,
      isExporting: false, // Track if export is in progress
      initialFilters: {},
    };
  },
  methods: {
    async fetchBalance() {
      this.balanceLoading = true;
      try {
        const response = await getBalance();
        if (response && response.status === "success" && response.data && response.data.merchant) {
          const merchant = response.data.merchant;
          // balance = API Balance, balance_dashboard = Dashboard Balance
          this.apiBalance = this.normalizeIndonesianNumber(merchant.balance) || 0;
          this.dashboardBalance = this.normalizeIndonesianNumber(merchant.balance_dashboard) || 0;
        } else {
          console.warn('Balance API response structure unexpected:', response);
          this.dashboardBalance = null;
          this.apiBalance = null;
        }
      } catch (error) {
        console.error('Error fetching balance:', error);
        this.dashboardBalance = null;
        this.apiBalance = null;
      } finally {
        this.balanceLoading = false;
      }
    },
    // API returns balance as an Indonesian formatted string, e.g. "398.759" -> 398759
    normalizeIndonesianNumber(value) {
      if (typeof value === "string") {
        value = value.replace(/\./g, "").replace(",", ".");
      }
      return Number(value);
    },
    async fetchDashboardVirtualAccounts() {
      try {
        const response = await getListVA("DASHBOARD");
        if (response && response.status === "success" && Array.isArray(response.data)) {
          this.hasDashboardVa = response.data.length > 0;
        }
      } catch (error) {
        // Keep the balance view on error rather than prompting to generate a VA
        console.error('Error fetching dashboard virtual accounts:', error);
      }
    },
    // TODO: Generate dashboard VAs for all available bank vendors; currently
    //       only Permata is generated
    async handleGenerateVa() {
      this.generatingVa = true;
      try {
        const response = await generateDashboardVA();
        if (response && response.status === "success") {
          this.hasDashboardVa = true;
          this.showToast("Your Virtual Account has been successfully created");
        } else {
          this.$swal({
            title: "Failed",
            text: (response && response.message) || "Failed to generate virtual account. Please try again.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      } catch (error) {
        console.error('Error generating dashboard virtual account:', error);
        this.$swal({
          title: "Failed",
          text: error.message || "Failed to generate virtual account. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        this.generatingVa = false;
      }
    },
    openAddBalance(appType) {
      this.addBalanceAppType = appType;
      this.showAddBalance = true;
    },
    openTransfer(source) {
      // Blocked merchants cannot transfer balance (also rejected by the backend)
      if (this.isMerchantBlocked) {
        this.$swal({
          title: "Transfer Not Allowed",
          text: "Your account is blocked. Transfer balance is not allowed.",
          icon: "warning",
          confirmButtonText: "OK",
        });
        return;
      }
      this.transferSource = source;
      this.showTransferBalance = true;
    },
    async handleTransferSubmit({ from, amount }) {
      this.transferringBalance = true;
      try {
        await transferBalance(from, amount);
        this.showTransferBalance = false;
        this.showToast("Transfer balance success");
        this.fetchBalance();
        if (this.$refs.balanceActivityTable) {
          this.$refs.balanceActivityTable.triggerServerFilter();
        }
      } catch (error) {
        console.error('Error transferring balance:', error);
        this.$swal({
          title: "Transfer Failed",
          text: error.message || "Failed to transfer balance. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        this.transferringBalance = false;
      }
    },
    showToast(message) {
      clearTimeout(this.toastTimer);
      this.toastMessage = message;
      this.toastTimer = setTimeout(() => {
        this.toastMessage = "";
      }, 3000);
    },
    async fetchHistoryBalance(page = 1, perPage = 20, filters = {}) {
      this.activitiesLoading = true;
      try {
        // Prepare pagination parameters
        const params = {
          page: page,
          per_page: perPage
        };

        // Add filter parameters if they exist
        if (filters.search && filters.search.trim() !== "") {
          params.ref_id = filters.search.trim();
        }

        Object.assign(params, this.buildTypeParams(filters));

        if (filters.dateRange && Array.isArray(filters.dateRange) && filters.dateRange.length === 2 && filters.dateRange[0] && filters.dateRange[1]) {
          const startDate = this.formatDateToAPI(new Date(filters.dateRange[0]));
          const endDate = this.formatDateToAPI(new Date(filters.dateRange[1]));
          params.date_range = `${startDate} - ${endDate}`;
        }

        const response = await historyBalance(params);
        if (response && response.status === "success" && response.data) {
          // Update activities data
          if (response.data.data) {
            this.activities = response.data.data;
          } else {
            console.warn('History balance API response missing data array:', response);
            this.activities = [];
          }

          // Update pagination data if available
          if (response.data.pagination) {
            this.pagination = response.data.pagination;
          }
        } else {
          console.warn('History balance API response structure unexpected:', response);
          this.activities = [];
        }
      } catch (error) {
        console.error('Error fetching balance history:', error);
        this.activities = [];
      } finally {
        this.activitiesLoading = false;
      }
    },

    // Handle page change from child component
    handlePageChange(page, perPage, filters = {}) {
      this.fetchHistoryBalance(page, perPage, filters);
    },

    // Map Type / Source of Fund multi-select filters to API params
    // Both accept comma-separated values; Source of Fund is filtered by app_type
    buildTypeParams(filters) {
      const params = {};
      if (Array.isArray(filters.types) && filters.types.length > 0) {
        params.source = filters.types.join(",");
      }
      if (Array.isArray(filters.sourceOfFund) && filters.sourceOfFund.length > 0) {
        params.app_type = filters.sourceOfFund.join(",");
      }
      return params;
    },

    // Format date to API format (YYYY-MM-DD)
    formatDateToAPI(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${day}/${month}/${year}`;
    },

    // Handle export CSV with filters
    async handleExportCSV(filters) {
      // Validasi: export hanya boleh untuk range 1 hari (tanggal mulai dan akhir sama)
      if (
        filters.dateRange &&
        Array.isArray(filters.dateRange) &&
        filters.dateRange.length === 2 &&
        filters.dateRange[0] &&
        filters.dateRange[1]
      ) {
        const startDate = new Date(filters.dateRange[0]);
        const endDate = new Date(filters.dateRange[1]);

        // Bandingkan hanya berdasarkan tanggal (tanpa jam)
        const isSameDay = startDate.toDateString() === endDate.toDateString();

        if (!isSameDay) {
          this.$swal({
            title: 'Invalid Date Range',
            text: 'Export hanya bisa untuk rentang 1 hari. Silakan pilih tanggal yang sama untuk Start dan End.',
            icon: 'warning',
            confirmButtonText: 'OK'
          });
          return;
        }
      }

      try {
        this.activitiesLoading = true;
        this.isExporting = true; // Set flag to hide default loading

        // Show loading alert for export process
        this.$swal({
          title: 'Preparing Export...',
          html: `
            <div style="text-align: center; padding: 20px 0;">
              <p style="margin: 10px 0; color: #495057; font-size: 14px;">
                Sedang memproses data balance activity untuk di-export...
              </p>
              <p style="margin: 10px 0; color: #6c757d; font-size: 12px;">
                Untuk data yang banyak, proses ini mungkin membutuhkan waktu beberapa saat.<br>
                Mohon tunggu dan jangan tutup halaman ini.
              </p>
            </div>
          `,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Build params for backend export API
        const params = {};

        // Add date range filter if set
        if (
          filters.dateRange &&
          Array.isArray(filters.dateRange) &&
          filters.dateRange.length === 2 &&
          filters.dateRange[0] &&
          filters.dateRange[1]
        ) {
          const startDate = this.formatDateToAPI(new Date(filters.dateRange[0]));
          const endDate = this.formatDateToAPI(new Date(filters.dateRange[1]));
          params.date_range = `${startDate} - ${endDate}`;
        }

        // Add type / source of fund filters if set
        Object.assign(params, this.buildTypeParams(filters));

        // Add ref_id filter if set
        if (filters.search && filters.search.trim() !== "") {
          params.ref_id = filters.search.trim();
        }

        // Show loading alert for download
        this.$swal({
          title: 'Downloading...',
          html: `
            <div style="text-align: center; padding: 20px 0;">
              <p style="margin: 10px 0; color: #495057; font-size: 14px;">
                Sedang download, mohon ditunggu...
              </p>
            </div>
          `,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Fetch data
        const backendApi =
          process.env.VUE_APP_BACKEND_API || "https://api.swiftrans.id";
        const token = localStorage.getItem("auth_token");
        let exportUrl = `${backendApi}/api/merchant/history-balances/export`;
        
        // Build query string
        const queryParams = new URLSearchParams();
        if (params.date_range) queryParams.append('date_range', params.date_range);
        if (params.source) queryParams.append('source', params.source);
        if (params.app_type) queryParams.append('app_type', params.app_type);
        if (params.ref_id) queryParams.append('ref_id', params.ref_id);
        
        if (queryParams.toString()) {
          exportUrl += `?${queryParams.toString()}`;
        }

        const response = await fetch(exportUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to download file');
        }

        // Get blob directly
        const blob = await response.blob();
        
        // Close progress alert
        this.$swal.close();

        if (!blob || blob.size === 0) {
          this.$swal({
            title: 'No Data',
            text: 'No data to export',
            icon: 'info',
            confirmButtonText: 'OK'
          });
          return;
        }

        // Generate informative filename
        const now = new Date();
        const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD format
        const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, ""); // HHMMSS format
        
        // Build filename with filter info
        let filterInfo = "";
        if (filters.types && filters.types.length) filterInfo += `_source-${filters.types.join("-")}`;
        if (filters.search) filterInfo += `_refid-${filters.search.substring(0, 10)}`;
        if (filters.dateRange && filters.dateRange.length === 2) {
          const startDate = new Date(filters.dateRange[0]);
          const endDate = new Date(filters.dateRange[1]);
          filterInfo += `_from-${startDate.toISOString().split("T")[0]}_to-${endDate.toISOString().split("T")[0]}`;
        }
        
        const filename = `balance-history${filterInfo}_${dateStr}_${timeStr}.csv`;

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        
        // Clean up
        window.URL.revokeObjectURL(url);
        
        // Show success message
        this.$swal({
          title: 'Success!',
          text: 'Balance activity exported successfully',
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      } catch (error) {
        console.error('Error exporting CSV:', error);
        // Close loading alert if still open
        this.$swal.close();
        this.$swal({
          title: 'Export Failed',
          text: error.message || 'Error exporting data. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.activitiesLoading = false;
        this.isExporting = false; // Reset flag to show default loading again
      }
    },
    stripTime(date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    },

    // Extract URL parameters for initial filtering
    extractUrlParameters() {
      const query = this.$route.query;
      const filters = {};

      if (query.ref_id) {
        filters.search = query.ref_id;
      }

      if (query.source) {
        filters.types = String(query.source).split(",");
      }

      if (query.app_type) {
        filters.sourceOfFund = String(query.app_type).split(",");
      }

      if (query.start_date && query.end_date) {
        filters.dateRange = [
          new Date(query.start_date),
          new Date(query.end_date)
        ];
      }

      return filters;
    },
  },
  mounted() {
    // Extract URL parameters for initial filtering
    this.initialFilters = this.extractUrlParameters();

    this.fetchBalance();
    this.fetchDashboardVirtualAccounts();
    // The BalanceActivityTable child component will handle the initial history fetch
    // with its default date filter (today) or with URL parameters if provided
  },
  beforeUnmount() {
    clearTimeout(this.toastTimer);
  },
};
</script>

<style scoped>
.balance-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.activity-card {
  margin-top: 16px;
  background: #fff;
  border: 1px solid #dae0e2;
  border-radius: 16px;
}

.activity-label {
  color: #022326;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  padding: 16px 24px 0;
}

.balance-toast {
  position: fixed;
  top: 76px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #a6e3bf;
  background: #e8f8ee;
  color: #022326;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
  font-size: 14px;
  white-space: nowrap;
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -8px);
}

@media (max-width: 992px) {
  .balance-cards {
    grid-template-columns: 1fr;
  }
}
</style>
